import { Directive, ElementRef, Inject, OnInit, Renderer2 } from '@angular/core';
import { MonitoringPageService } from '../page/monitoring-page.service';
import { DOCUMENT } from '@angular/common';
import { fromEvent, Observable, merge, Subscription } from 'rxjs';
import { MatDrawer } from '@angular/material/sidenav';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { AutoUnsubscribe } from '../../../../common/decorators';

type FilterHeaderConfigType = {
  sticky: boolean;
  width: string;
  height: string;
};

@Directive({
  selector: '[filtersStickyHeader]',
})
@AutoUnsubscribe
export class MonitoringFiltersStickyHeaderDirective implements OnInit {
  private readonly STICKY_STYLES: { [key: string]: string } = {
    position: 'fixed',
    top: '0px',
    background: '#f7f7f7',
  };
  private subscriptions: Subscription[] = [];

  private stickyOnResize$ = this.monitoringPageService.matDrawer$.pipe(
    debounceTime(0),
    map((drawer: MatDrawer) => {
      return !drawer && this.elementRef.nativeElement.parentNode.getBoundingClientRect().y < 0;
    }),
  );

  private stickyOnScroll$ = fromEvent(this.document.defaultView, 'scroll').pipe(
    map(() => {
      return this.elementRef.nativeElement.parentNode.getBoundingClientRect().y < 0;
    }),
  );

  private config$: Observable<FilterHeaderConfigType> = merge(this.stickyOnResize$, this.stickyOnScroll$).pipe(
    distinctUntilChanged(),
    map((sticky: boolean) => {
      const { offsetHeight } = this.elementRef.nativeElement;
      const { offsetWidth } = this.elementRef.nativeElement.parentNode;

      return {
        sticky,
        width: !sticky ? '100%' : `${offsetWidth}px`,
        height: sticky ? `${offsetHeight}px` : '0px',
      };
    }),
    distinctUntilChanged(
      (previous: FilterHeaderConfigType, current: FilterHeaderConfigType) =>
        previous.width === current.width && previous.height === current.height,
    ),
  );

  constructor(
    private elementRef: ElementRef,
    private monitoringPageService: MonitoringPageService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.config$.subscribe((config: FilterHeaderConfigType) => {
        Object.entries(this.STICKY_STYLES).forEach(([key, value]: [string, string]) =>
          config.sticky
            ? this.renderer.setStyle(this.elementRef.nativeElement, key, value)
            : this.renderer.removeStyle(this.elementRef.nativeElement, key),
        );

        this.renderer.setStyle(this.elementRef.nativeElement.parentNode, 'padding-top', config.height);
        this.renderer.setStyle(this.elementRef.nativeElement, 'width', config.width);
      }),
    );
  }
}
